import { useMemo } from 'react'
import { WEEK_LABELS } from './inboundFlowReportMockData'

interface Props {
  series: Array<number | null>
  metricLabel: string
}

const WIDTH = 1180
const HEIGHT = 300
const PAD_LEFT = 56
const PAD_RIGHT = 16
const PAD_TOP = 36
const PAD_BOTTOM = 26

// Colors approximated from the reference screenshot's blue area/line chart
// (not the app's teal palette) — matching the source design's own semantics.
const LINE_COLOR = '#3b7ddb'
const AREA_COLOR = 'rgba(59, 125, 219, 0.25)'
const GRID_COLOR = '#e7eaef'
const LABEL_COLOR = '#1c1c1c'
const AXIS_TICK_COLOR = '#6b7280'

interface Scale {
  divisor: number
  suffix: string
}

function pickScale(maxAbs: number): Scale {
  if (maxAbs >= 1_000_000) return { divisor: 1_000_000, suffix: 'M' }
  if (maxAbs >= 1_000) return { divisor: 1_000, suffix: 'K' }
  return { divisor: 1, suffix: '' }
}

function formatWithScale(value: number, scale: Scale): string {
  if (scale.divisor === 1) return Math.round(value).toLocaleString('en-US')
  return `${(value / scale.divisor).toFixed(2)}${scale.suffix}`
}

// Rounds up to the nearest "nice" number (1/2/5 * 10^n) for gridline scaling.
function niceCeiling(value: number): number {
  if (value <= 0) return 1
  const exponent = Math.floor(Math.log10(value))
  const fraction = value / Math.pow(10, exponent)
  let niceFraction: number
  if (fraction <= 1) niceFraction = 1
  else if (fraction <= 2) niceFraction = 2
  else if (fraction <= 5) niceFraction = 5
  else niceFraction = 10
  return niceFraction * Math.pow(10, exponent)
}

export function InboundFlowChart({ series, metricLabel }: Props) {
  const { points, yMax, scale, plotW, plotH } = useMemo(() => {
    const values = series.map(v => v ?? 0)
    const maxVal = Math.max(...values, 0)
    const scale = pickScale(maxVal)
    const yMax = niceCeiling(maxVal || 1)
    const plotW = WIDTH - PAD_LEFT - PAD_RIGHT
    const plotH = HEIGHT - PAD_TOP - PAD_BOTTOM
    const slot = values.length > 1 ? plotW / (values.length - 1) : plotW

    const points = values.map((v, i) => ({
      x: i * slot,
      y: plotH - (v / yMax) * plotH,
      value: v,
      week: WEEK_LABELS[i],
    }))

    return { points, yMax, scale, plotW, plotH }
  }, [series])

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(' ')
  const areaPath = `${linePath} L ${points[points.length - 1]?.x.toFixed(2)} ${plotH} L ${points[0]?.x.toFixed(2)} ${plotH} Z`

  const yTicks = [0, yMax / 2, yMax]

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-white p-4 shadow-sm">
      <div className="mb-2 text-sm font-semibold text-foreground">
        Projected Inbound Flow – {metricLabel}
      </div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        width="100%"
        height={HEIGHT}
        preserveAspectRatio="none"
      >
        <g transform={`translate(${PAD_LEFT}, ${PAD_TOP})`}>
          {yTicks.map(tick => {
            const y = plotH - (tick / yMax) * plotH
            return (
              <g key={tick}>
                <line
                  x1={0}
                  x2={plotW}
                  y1={y}
                  y2={y}
                  stroke={GRID_COLOR}
                  strokeWidth={1}
                  strokeDasharray={tick === 0 ? undefined : '3 3'}
                />
                <text
                  x={-8}
                  y={y + 3}
                  textAnchor="end"
                  fontSize={10}
                  fill={AXIS_TICK_COLOR}
                >
                  {formatWithScale(tick, scale)}
                </text>
              </g>
            )
          })}

          <path d={areaPath} fill={AREA_COLOR} stroke="none" />
          <path d={linePath} fill="none" stroke={LINE_COLOR} strokeWidth={2} />

          {points.map(p => (
            <g key={p.week}>
              <circle cx={p.x} cy={p.y} r={2.5} fill={LINE_COLOR} />
              <text
                x={p.x}
                y={Math.max(10, p.y - 8)}
                textAnchor="middle"
                fontSize={9}
                fill={LABEL_COLOR}
              >
                {formatWithScale(p.value, scale)}
              </text>
              <text
                x={p.x}
                y={plotH + 16}
                textAnchor="middle"
                fontSize={9}
                fill={AXIS_TICK_COLOR}
              >
                {p.week}
              </text>
            </g>
          ))}
        </g>
      </svg>
    </div>
  )
}
